import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { detectFields } from './detectFields';

function FieldRow({ label, value, isLong }) {
  return (
    <Box sx={{
      gridColumn: isLong ? '1 / -1' : 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
    }}>
      <Typography component="div" sx={{
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        color: 'text.secondary',
        fontWeight: 600,
      }}>{label}</Typography>
      <Typography component="div" sx={{
        fontSize: '13px',
        color: 'text.primary',
        wordBreak: 'break-word',
        ...(isLong && {
          backgroundColor: 'grey.50',
          padding: '8px 10px',
          borderRadius: '4px',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
        }),
      }}>{value}</Typography>
    </Box>
  );
}

export default function SubmissionCompactRow({ submission, formType }) {
  const [expanded, setExpanded] = useState(false);
  const detected = useMemo(() => detectFields(submission, formType), [submission, formType]);
  const { entryId, name, email, message, date, rest } = detected;

  const headerLabel = name || (entryId ? `Entry #${entryId}` : 'Submission');
  const messageIsLong = message && (message.includes('\n') || message.length > 100);
  const hasExpandableContent = rest.length > 0 || messageIsLong;

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'grey.100', '&:last-child': { borderBottom: 'none' } }}>
      <Box
        role={hasExpandableContent ? 'button' : undefined}
        tabIndex={hasExpandableContent ? 0 : -1}
        aria-label={hasExpandableContent ? `Submission from ${headerLabel} — click to expand` : undefined}
        aria-expanded={hasExpandableContent ? expanded : undefined}
        onClick={() => hasExpandableContent && setExpanded(e => !e)}
        onKeyDown={(e) => {
          if (!hasExpandableContent) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(x => !x);
          }
        }}
        sx={{
          display: 'grid',
          gridTemplateColumns: '16px 1fr 1fr 1.5fr 90px',
          gap: '10px',
          padding: '6px 14px',
          alignItems: 'center',
          fontSize: '11px',
          cursor: hasExpandableContent ? 'pointer' : 'default',
          userSelect: 'none',
          '&:hover': hasExpandableContent ? { backgroundColor: 'grey.50' } : {},
        }}
      >
        <Typography component="span" sx={{ color: 'text.secondary', fontSize: '10px' }}>
          {hasExpandableContent ? (expanded ? '▾' : '▸') : ''}
        </Typography>
        <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '11px' }}>{headerLabel}</Typography>
        <Typography component="span" sx={{ color: 'text.secondary', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email || ''}</Typography>
        <Typography component="span" sx={{ color: 'text.secondary', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{message || ''}</Typography>
        <Typography component="span" sx={{ color: 'text.secondary', fontSize: '10px', textAlign: 'right' }}>{date ? date.label : ''}</Typography>
      </Box>

      {expanded && hasExpandableContent && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px 24px',
          padding: '12px 16px 16px 46px',
          backgroundColor: 'grey.50',
        }}>
          {rest.map(f => (
            <FieldRow key={f.key} label={f.label} value={f.value} isLong={f.isLong} />
          ))}
          {messageIsLong && (
            <FieldRow label="Full Message" value={message} isLong={true} />
          )}
        </Box>
      )}
    </Box>
  );
}
